import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';

/**
 * Hook to determine if the current values differ from the initial values.
 * It's optimized to avoid expensive equality checks if we've already determined that the values are dirty.
 */
export function useIsModified({ initialValues, values }: { initialValues: unknown; values: unknown }) {
  const isDirtyRef = useRef(false);
  useEffect(() => {
    // Reset the dirty flag when the initial values change
    isDirtyRef.current = false;
  }, [initialValues]);

  // Short circuit if we've already determined that the values are dirty to avoid expensive equality checks
  if (isDirtyRef.current) {
    return true;
  }

  // The values must be set before we can determine if there are changes
  if (initialValues && values) {
    isDirtyRef.current = hasChanges(initialValues, values);
  }

  return isDirtyRef.current;
}

/**
 * Compares two objects and returns true if they are different.
 * It accounts for nested objects and undefined values.
 * Under the hood, it reserializes the objects to JSON and compares the result using lodash's isEqual.
 */
export function hasChanges(a: unknown, b: unknown): boolean {
  return !isEqual(reserializeObject(a), reserializeObject(b));
}

function reserializeObject(obj: unknown) {
  return JSON.parse(JSON.stringify(obj ?? ''));
}
